'use client';

import { setStage, Stage } from '@3fourteen/core';
import { NotifiableError } from '@bugsnag/js';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { mainBugsnag, MainErrorBoundary } from 'helpers/bugsnag';
import React, { Suspense, useCallback, useEffect, useState } from 'react';

import { SearchModal } from 'components/SearchModal';

import { AuthProvider, ProtectRoute, useAuth } from '../contexts/auth';
import { HistoryProvider } from '../contexts/history';
import { ToastProvider } from '../contexts/toast';
import { IS_PRODUCTION } from '../services/environment';

setStage(IS_PRODUCTION ? Stage.prod : Stage.dev);

const staleTime = 4 * 60 * 1000;

export default function Providers({ children }) {
  const { isAuthenticated } = useAuth();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            retry: 1,
            // staleTime,
            enabled: isAuthenticated,
          },
        },
        queryCache: new QueryCache({
          onError: (error, query) => {
            mainBugsnag.notify(error as NotifiableError, (event) => {
              event.context = 'QueryCache';
              event.addMetadata("Add'l Info", {
                query,
              });
            });
          },
        }),
      })
  );

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      setIsSearchOpen(true);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <MainErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <HistoryProvider>
          <AuthProvider>
            <ToastProvider>
              <ProtectRoute>
                {children}
                <Suspense fallback={null}>
                  <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
                </Suspense>
              </ProtectRoute>
            </ToastProvider>
          </AuthProvider>
        </HistoryProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </MainErrorBoundary>
  );
}
